"use client";

import * as React from "react";
import { useState, useEffect, useMemo, useRef, useId } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  PaginationState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleX,
  Columns3,
  Ellipsis,
  Plus,
  Trash,
  Phone,
  Mail,
  DollarSign,
  Clock,
  Edit,
  UserPlus,
  ListFilter
} from "lucide-react";
import type { Contact, Lead, Application, Borrower } from "@/types/contacts-new";
import { __simpleContactService, type SimpleContact } from "@/services/contactService-simple";
import { BulkAssignDialog } from "./BulkAssignDialog";

import logger from '@/utils/logger';
import { formatPhoneNumber } from '@/utils/phoneFormatting';
import { parseAsLocalDate } from '@/utils/dateHelpers';

const confirmContactDeletion = (message: string): boolean => {
  // Preserve the existing native confirmation until the contacts table adopts a shared alert dialog.
  // eslint-disable-next-line no-alert
  return window.confirm(message);
};

// Types
interface ContactRow {
  id: string;
  contactId?: string; // The actual contact ID from the contacts table
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  location?: string;
  type: string;
  stage: string;
  status?: string; // Mapped status for display
  lastContact?: Date;
  notes?: string;
  engagementScore?: number;
  // Additional fields from your data
  loanAmount?: number;
  creditScore?: number;
  propertyValue?: number;
  source?: string; // "Lead", "Borrower", "Manual Contact"
  tags?: string[];
  originalLeadStatus?: string; // Keep original status for filtering
  // Loan history fields
  totalLoans?: number;
  totalFundedAmount?: number;
  lastLoanDate?: string;
  loanHistory?: Array<{
    id: string;
    loan_number?: string;
    loan_amount?: number;
    closing_date?: string;
    status?: string;
  }>;
}

interface ContactsPipelineTableProps {
  // Legacy props for backward compatibility
  contacts?: Contact[];
  leads?: Lead[];
  applications?: Application[];
  borrowers?: Borrower[];
  // New simplified props
  simpleContacts?: SimpleContact[];
  contactsWithLoans?: (SimpleContact & {
    loanHistory: Array<{
      id: string;
      contact_id: string;
      loan_number: string;
      funded_amount: number;
      interest_rate: number;
      closing_date: string;
      property_address: string;
      is_current: boolean;
      lifetime_loan_count: number;
    }>;
    isPastCustomer: boolean;
    totalLoans: number;
    totalFundedAmount: number;
    lastLoanDate?: string;
  })[];
  onAddContact?: () => void;
  onEditContact?: (contact: ContactRow) => void;
  onDeleteContact?: (contactId: string) => Promise<void>;
  onBulkDeleteContacts?: (contactIds: string[]) => Promise<void>;
  onBulkAssignContacts?: (contactIds: string[], officerId: string) => Promise<void>;
  onRefresh?: () => void;
  filterType?: 'activeLeads' | 'pastCustomers' | 'refiReady' | 'allContacts';
}

/**
 * Map lead status to custom values
 */
const mapLeadStatus = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'new':
      return 'New';
    case 'contacted':
    case 'qualifying':
    case 'nurturing':
    case 'quote_sent':
    case 'pre_approved':
      return 'Hot';
    case 'converted':
      return 'Convert';
    case 'lost':
      return 'Bad Information';
    default:
      return status || 'Cool'; // Cool is a manual override for disengaged leads
  }
};

/**
 * Get badge color for custom status
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case "New": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "Hot": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    case "Cool": return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300";
    case "Convert": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "Bad Information": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

/**
 * Calculate engagement score based on behavioral data
 */
const calculateEngagementScore = (contact: ContactRow): number => {
  let score = 0;

  // Recent contact is valuable
  if (contact.lastContact) {
    const daysSinceContact = Math.floor(
      (Date.now() - contact.lastContact.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceContact < 7) {
score += 30;
} else if (daysSinceContact < 30) {
score += 20;
} else if (daysSinceContact < 90) {
score += 10;
}
  }
  

  return Math.min(score, 100);
};

/**
 * Convert SimpleContact to ContactRow format
 */
const convertSimpleContactToRow = (
  contact: SimpleContact & { 
    loanHistory?: Array<{
    id: string;
    loan_number?: string;
    loan_amount?: number;
    closing_date?: string;
    status?: string;
  }>; 
    isPastCustomer?: boolean; 
    totalLoans?: number; 
    totalFundedAmount?: number; 
    lastLoanDate?: string;
  }
): ContactRow => {
  // Determine type based on loan history, not just contact type
  const type = contact.isPastCustomer 
    ? 'Past Customer' 
    : contact.contactType === 'referral_partner' 
      ? 'Referral Partner' 
      : 'Active Lead';
  
  return {
    id: contact.id,
    contactId: contact.id,
    firstName: contact.firstName || '',
    lastName: contact.lastName || '',
    email: contact.email || '',
    phone: contact.mobilePhone || '',
    company: contact.company,
    location: contact.city && contact.state ? `${contact.city}, ${contact.state}` : undefined,
    type,
    stage: contact.isPastCustomer ? 'Borrower' : 
           contact.contactType === 'referral_partner' ? 'Partner' : 'Lead',
    status: mapLeadStatus(contact.status || 'new'),
    originalLeadStatus: contact.status,
    lastContact: contact.lastContactDate ? parseAsLocalDate(contact.lastContactDate) :
                contact.updatedAt ? parseAsLocalDate(contact.updatedAt) : undefined,
    notes: contact.notes,
    loanAmount: contact.loanAmount,
    creditScore: contact.creditScore,
    source: contact.source || 'Simple Contact',
    tags: contact.tags || [],
    // Add loan history data
    totalLoans: contact.totalLoans || 0,
    totalFundedAmount: contact.totalFundedAmount || 0,
    lastLoanDate: contact.lastLoanDate,
    loanHistory: contact.loanHistory || []
  };
};

/**
 * Process simplified contacts
 */
const processSimpleContacts = (
  simpleContacts: (SimpleContact & { 
    loanHistory?: Array<{
    id: string;
    loan_number?: string;
    loan_amount?: number;
    closing_date?: string;
    status?: string;
  }>; 
    isPastCustomer?: boolean; 
    totalLoans?: number; 
    totalFundedAmount?: number;
    lastLoanDate?: string;
  })[],
  _filterType?: string
): ContactRow[] => {
  // Contacts are already filtered by the parent component based on loan history
  // No need for additional filtering here
  return simpleContacts
    .map(contact => convertSimpleContactToRow(contact))
    .map(row => ({
      ...row,
      engagementScore: calculateEngagementScore(row)
    }));
};

/**
 * Merges and deduplicates contacts across multiple data sources
 * Priority: Borrower > Application > Lead > Contact
 */
const mergeAndDeduplicateContacts = (
  contacts: Contact[],
  leads: Lead[],
  applications: Application[],
  borrowers: Borrower[]
): ContactRow[] => {
  const mergedMap = new Map<string, ContactRow>();

  /**
   * Generate unique key for deduplication
   * Prioritizes email, then phone (with optional name for specificity)
   */
  const generateKey = (
    email?: string,
    phone?: string,
    firstName?: string,
    lastName?: string
  ): string | null => {
    if (email && email.trim()) {
      return email.toLowerCase().trim();
    }
    // Allow phone-only dedup (no name requirement)
    if (phone && phone.trim()) {
      const normalizedPhone = phone.replace(/\D/g, '');
      if (normalizedPhone.length >= 10) {
        // Include name if available for more specific keys
        if (firstName || lastName) {
          const name = `${firstName || ''} ${lastName || ''}`.trim().toLowerCase();
          return `${normalizedPhone}-${name}`;
        }
        return normalizedPhone; // Phone-only key
      }
    }
    return null;
  };
  
  /**
   * Check if entry has meaningful data
   */
  const isValidEntry = (entry: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  }): boolean => {
    // Filter out test data
    if (entry.email?.includes('test@') || entry.email?.includes('example@')) {
      return false;
    }
    
    // Must have at least one identifier
    return Boolean(
      (entry.firstName && entry.firstName.trim()) ||
      (entry.lastName && entry.lastName.trim()) ||
      (entry.email && entry.email.trim()) ||
      (entry.phone && entry.phone.trim())
    );
  };
  
  // Process contacts (lowest priority)
  contacts.forEach(contact => {
    if (!isValidEntry(contact)) {
      return;
}
    
    const key = generateKey(
      contact.email,
      contact.mobilePhone,
      contact.firstName,
      contact.lastName
    );

    if (!key) {
      return;
}

    const row: ContactRow = {
      id: contact.id,
      contactId: contact.id, // For contacts, the ID is the contact ID
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.mobilePhone || '',
      company: contact.company,
      location: contact.mailingCity && contact.mailingState 
        ? `${contact.mailingCity}, ${contact.mailingState}` 
        : undefined,
      type: 'Contact',
      stage: 'Contact',
      status: 'Contact',
      lastContact: contact.updatedAt ? parseAsLocalDate(contact.updatedAt) : undefined,
      notes: contact.notes,
      source: 'Manual Contact',
      tags: contact.tags
    };
    
    mergedMap.set(key, row);
  });
  
  // Process leads (higher priority)
  leads.forEach(lead => {
    // Extract contact info from nested structure
    const contact = lead.contact || {};
    const leadEntry = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.mobilePhone
    };

    if (!isValidEntry(leadEntry)) {
      return;
}

    const key = generateKey(
      contact.email,
      contact.mobilePhone,
      contact.firstName,
      contact.lastName
    );

    if (!key) {
      return;
}

    const existing = mergedMap.get(key);

    const row: ContactRow = {
      id: lead.contactId, // Always use the contact ID for consistency
      contactId: lead.contactId, // Always use the actual contact ID
      firstName: contact.firstName || existing?.firstName || '',
      lastName: contact.lastName || existing?.lastName || '',
      email: contact.email || existing?.email || '',
      phone: contact.mobilePhone || '',
      company: existing?.company,
      location: existing?.location,
      type: 'Lead',
      stage: 'Lead',
      status: mapLeadStatus(lead.status),
      originalLeadStatus: lead.status,
      lastContact: lead.lastContactDate
        ? parseAsLocalDate(lead.lastContactDate)
        : existing?.lastContact,
      notes: lead.notes || existing?.notes,
      loanAmount: lead.estimatedLoanAmount,
      source: 'Lead',
      tags: [...(existing?.tags || []), ...(contact.tags || [])]
    };
    
    mergedMap.set(key, row);
  });
  
  // Process applications (higher priority)
  applications.forEach(app => {
    if (!app.borrowerEmail) {
      return;
}
    
    const key = app.borrowerEmail.toLowerCase();
    const existing = mergedMap.get(key);
    
    if (existing) {
      existing.stage = 'Application';
      existing.loanAmount = app.loanAmount || existing.loanAmount;
      existing.type = 'Application';
      existing.source = 'Application';
    }
  });
  
  // Process borrowers (highest priority)
  borrowers.forEach(borrower => {
    // Extract contact info from nested structure
    const contact = borrower.contact || {};
    const borrowerEntry = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.mobilePhone
    };

    if (!isValidEntry(borrowerEntry)) {
      return;
}

    const key = generateKey(
      contact.email,
      contact.mobilePhone,
      contact.firstName,
      contact.lastName
    );

    if (!key) {
      return;
}

    const existing = mergedMap.get(key);

    const row: ContactRow = {
      id: borrower.contactId, // Always use the contact ID for consistency
      contactId: borrower.contactId, // Always use the actual contact ID
      firstName: contact.firstName || existing?.firstName || '',
      lastName: contact.lastName || existing?.lastName || '',
      email: contact.email || existing?.email || '',
      phone: contact.mobilePhone || '',
      company: existing?.company,
      location: existing?.location,
      type: 'Past Customer', // All borrowers are past customers
      stage: 'Borrower',
      status: existing?.status || 'Past Customer',
      originalLeadStatus: existing?.originalLeadStatus,
      lastContact: borrower.updatedAt
        ? parseAsLocalDate(borrower.updatedAt)
        : existing?.lastContact,
      notes: borrower.notes || existing?.notes,
      loanAmount: borrower.fundedAmount || existing?.loanAmount,
      creditScore: borrower.creditScore,
      propertyValue: borrower.appraisedValue,
      source: 'Borrower',
      tags: [...(existing?.tags || []), ...(contact.tags || [])]
    };
    
    mergedMap.set(key, row);
  });
  
  // Convert map to array and calculate engagement scores
  return Array.from(mergedMap.values()).map(row => ({
    ...row,
    engagementScore: calculateEngagementScore(row)
  }));
};

// Custom filter functions
const multiColumnFilterFn: FilterFn<ContactRow> = (row, columnId, filterValue) => {
  const searchableRowContent = `${row.original.firstName} ${row.original.lastName} ${row.original.email} ${row.original.company || ''}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

// Stage colors
const _getStageColor = (stage: string) => {
  switch (stage) {
    case "Lead": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "Application": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    case "Borrower": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

// Row Actions Component
function RowActions({ 
  row, 
  onEditContact,
  onDeleteContact 
}: { 
  row: Row<ContactRow>, 
  onEditContact?: (contact: ContactRow) => void,
  onDeleteContact?: (contactId: string) => Promise<void>
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex justify-end">
          <Button size="icon" variant="ghost" className="shadow-none" aria-label="Actions">
            <Ellipsis size={16} strokeWidth={2} aria-hidden="true" />
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => onEditContact?.(row.original)}>
            <Edit className="mr-2 h-4 w-4" />
            <span>Edit Contact</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          className="text-destructive focus:text-destructive"
          onClick={async () => {
            const contactId = row.original.contactId || row.original.id;
            
            if (onDeleteContact && contactId && confirmContactDeletion(`Are you sure you want to delete ${row.original.firstName} ${row.original.lastName}?`)) {
              try {
                await onDeleteContact(contactId);
              } catch (error) {
                logger.error('Failed to delete contact:', error);
              }
            }
          }}
        >
          <Trash className="mr-2 h-4 w-4" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const ContactsPipelineTable: React.FC<ContactsPipelineTableProps> = ({
  contacts = [],
  leads = [],
  applications = [],
  borrowers = [],
  simpleContacts,
  __contactsWithLoans,
  onAddContact,
  onEditContact,
  onDeleteContact,
  onBulkDeleteContacts,
  onBulkAssignContacts,
  __onRefresh,
  filterType
}) => {
  logger.info('ContactsPipelineTable: Component render', { 
    filterType,
    simpleContactsCount: simpleContacts?.length,
    timestamp: new Date().toISOString()
  });
  
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Table columns - defined inside component to access props
  const columns: ColumnDef<ContactRow>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      size: 28,
      enableSorting: false,
      enableHiding: false
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="font-medium">{`${row.original.firstName} ${row.original.lastName}`}</div>
          {row.original.company && (
            <div className="text-sm text-muted-foreground">{row.original.company}</div>
          )}
        </div>
      ),
      size: 200,
      filterFn: multiColumnFilterFn,
      enableHiding: false
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        if (!status) {
return null;
}
        return (
          <Badge className={getStatusColor(status)}>
            {status}
          </Badge>
        );
      },
      size: 120,
      filterFn: (row, id, value) => {
        if (!value) {
return true;
}
        return row.getValue(id) === value;
      }
    },
    {
      accessorKey: "contact",
      header: "Contact",
      cell: ({ row }) => (
        <div className="flex flex-col space-y-1">
          <div className="flex items-center space-x-2">
            <Mail className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">{row.original.email}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Phone className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">{formatPhoneNumber(row.original.phone || '')}</span>
          </div>
        </div>
      ),
      size: 250
    },
    {
      accessorKey: "loanAmount",
      header: "Loan Amount",
      cell: ({ row }) => {
        const amount = row.getValue("loanAmount") as number;
        if (!amount) {
return <span className="text-muted-foreground">-</span>;
}
        return (
          <div className="flex items-center space-x-1">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            <span>{new Intl.NumberFormat("en-US").format(amount)}</span>
          </div>
        );
      },
      size: 120
    },
    {
      accessorKey: "totalLoans",
      header: "Total Loans",
      cell: ({ row }) => {
        const count = row.getValue("totalLoans") as number;
        if (!count || count === 0) {
return <span className="text-muted-foreground">-</span>;
}
        return (
          <Badge variant="secondary" className="font-medium">
            {count} {count === 1 ? 'loan' : 'loans'}
          </Badge>
        );
      },
      size: 100
    },
    {
      accessorKey: "totalFundedAmount",
      header: "Total Funded",
      cell: ({ row }) => {
        const amount = row.getValue("totalFundedAmount") as number;
        if (!amount || amount === 0) {
return <span className="text-muted-foreground">-</span>;
}
        return (
          <div className="flex items-center space-x-1">
            <DollarSign className="h-3 w-3 text-green-500" />
            <span className="font-medium text-green-700 dark:text-green-400">
              {new Intl.NumberFormat("en-US", {
                notation: "compact",
                maximumFractionDigits: 1
              }).format(amount)}
            </span>
          </div>
        );
      },
      size: 120
    },
    {
      accessorKey: "lastLoanDate",
      header: "Last Loan",
      cell: ({ row }) => {
        const date = row.getValue("lastLoanDate") as string;
        if (!date) {
return <span className="text-muted-foreground">-</span>;
}
        
        const loanDate = parseAsLocalDate(date);
        const monthsAgo = Math.floor((Date.now() - loanDate.getTime()) / (1000 * 60 * 60 * 24 * 30));

        return (
          <div className="text-sm">
            <div>{loanDate.toLocaleDateString()}</div>
            <div className="text-xs text-muted-foreground">
              {monthsAgo === 0 ? 'This month' : 
               monthsAgo === 1 ? '1 month ago' : 
               `${monthsAgo} months ago`}
            </div>
          </div>
        );
      },
      size: 120
    },
    {
      accessorKey: "lastContact",
      header: "Last Contact",
      cell: ({ row }) => {
        const date = row.getValue("lastContact") as Date;
        if (!date) {
return <span className="text-muted-foreground">Never</span>;
}
        
        const daysDiff = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
        const formatted = date.toLocaleDateString();
        
        return (
          <div className="flex items-center space-x-2">
            <Clock className={cn(
              "h-3 w-3",
              daysDiff < 7 ? "text-green-500" : 
              daysDiff < 30 ? "text-yellow-500" : 
              "text-muted-foreground"
            )} />
            <span className="text-sm">{formatted}</span>
          </div>
        );
      },
      size: 120
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => <RowActions row={row} onEditContact={onEditContact} onDeleteContact={onDeleteContact} />,
      size: 60,
      enableHiding: false
    }
  ];
  
  // Convert your data to table format based on filter type
  const tableData = useMemo(() => {
    // Use simplified contacts if available
    if (simpleContacts && simpleContacts.length > 0) {
      return processSimpleContacts(simpleContacts, filterType);
    }
    
    // Legacy: Handle different filter types for old data structure
    switch (filterType) {
      case 'activeLeads': {
        // Only show leads NOT converted or lost, with deduplication by contact
        const activeLeadsMap = new Map<string, ContactRow>();
        
        leads
          .filter(lead => {
            // Check if lead has contact data
            const hasContactData = lead.contact ? 
              (lead.contact.firstName || lead.contact.email || lead.contact.mobilePhone) :
              false;
            
            return lead.status !== 'converted' && 
              lead.status !== 'lost' && 
              hasContactData;
          })
          .forEach(lead => {
            // Extract contact info from nested contact object
            const contact = lead.contact || {};
            const email = contact.email || '';
            
            // Create deduplication key (same logic as main merge function)
            let key = '';
            if (email && email.trim()) {
              key = email.toLowerCase().trim();
            } else if (contact.mobilePhone && (contact.firstName || contact.lastName)) {
              const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim().toLowerCase();
              key = `${contact.mobilePhone.replace(/\D/g, '')}-${name}`;
            } else {
              key = lead.contactId || lead.id; // Fallback to IDs
            }
            
            // Deduplication logic
            
            // Only keep the most recent lead for each contact
            const existing = activeLeadsMap.get(key);
            const leadDate = lead.lastContactDate ? parseAsLocalDate(lead.lastContactDate) : parseAsLocalDate(lead.createdAt || '1970-01-01');
            const existingDate = existing?.lastContact || new Date(0);
            
            if (!existing || leadDate > existingDate) {
              const row: ContactRow = {
                id: lead.contactId, // Use contact ID for consistency
                contactId: lead.contactId, // Explicitly set contactId
                firstName: contact.firstName || '',
                lastName: contact.lastName || '',
                email,
                phone: contact.mobilePhone || '',
                company: undefined,
                location: undefined,
                type: 'Lead',
                stage: 'Lead',
                status: mapLeadStatus(lead.status),
                originalLeadStatus: lead.status,
                source: 'Lead',
                lastContact: leadDate,
                notes: lead.notes,
                loanAmount: lead.estimatedLoanAmount,
                engagementScore: 0
              };

              activeLeadsMap.set(key, row);
            }
          });
        
        const result = Array.from(activeLeadsMap.values()).map(row => ({
          ...row,
          engagementScore: calculateEngagementScore(row)
        }));
        
        return result;
      }
      
      case 'pastCustomers': {
        // Only funded borrowers, with deduplication by contact
        const pastCustomersMap = new Map<string, ContactRow>();
        
        borrowers
          .filter(b => b.isCurrent) // Use isCurrent flag instead of loanStatus
          .forEach(borrower => {
            const contact = borrower.contact || {};
            const email = contact.email || '';
            
            // Create deduplication key (same logic as main merge function)
            let key = '';
            if (email && email.trim()) {
              key = email.toLowerCase().trim();
            } else if (contact.mobilePhone && (contact.firstName || contact.lastName)) {
              const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim().toLowerCase();
              key = `${contact.mobilePhone.replace(/\D/g, '')}-${name}`;
            } else {
              key = borrower.contactId || borrower.id; // Fallback to IDs
            }
            
            // Deduplication logic
            
            // Only keep the most recent borrower record for each contact
            const existing = pastCustomersMap.get(key);
            const borrowerDate = borrower.updatedAt ? parseAsLocalDate(borrower.updatedAt) : parseAsLocalDate(borrower.createdAt || '1970-01-01');
            const existingDate = existing?.lastContact || new Date(0);
            
            if (!existing || borrowerDate > existingDate) {
              const row: ContactRow = {
                id: borrower.contactId, // Use contact ID for consistency
                contactId: borrower.contactId, // Explicitly set contactId
                firstName: contact.firstName || '',
                lastName: contact.lastName || '',
                email,
                phone: contact.mobilePhone || '',
                company: undefined,
                location: undefined,
                type: 'Past Customer',
                stage: 'Borrower',
                status: 'Past Customer',
                source: 'Borrower',
                lastContact: borrowerDate,
                notes: borrower.notes,
                loanAmount: borrower.fundedAmount,
                creditScore: borrower.creditScore,
                propertyValue: borrower.appraisedValue,
                engagementScore: 0
              };

              pastCustomersMap.set(key, row);
            }
          });
        
        const pastCustomersResult = Array.from(pastCustomersMap.values()).map(row => ({
          ...row,
          engagementScore: calculateEngagementScore(row)
        }));
        
        return pastCustomersResult;
      }
      
      case 'refiReady':
        // Contacts tagged for refinancing
        return contacts
          .filter(c => 
            c.tags?.includes('refi-ready') || 
            c.tags?.includes('refinance-opportunity')
          )
          .map(contact => ({
            id: contact.id,
            contactId: contact.id, // For contacts, ID is the contact ID
            firstName: contact.firstName || '',
            lastName: contact.lastName || '',
            email: contact.email || '',
            phone: contact.mobilePhone || '',
            company: contact.company,
            location: contact.mailingCity && contact.mailingState 
              ? `${contact.mailingCity}, ${contact.mailingState}` 
              : undefined,
            type: 'Refinance',
            stage: 'Opportunity',
            status: 'Refi Ready',
            source: 'Manual Contact',
            lastContact: contact.updatedAt ? parseAsLocalDate(contact.updatedAt) : undefined,
            notes: contact.notes,
            engagementScore: 0
          }));

      case 'allContacts':
      default:
        // Merge and deduplicate all sources
        return mergeAndDeduplicateContacts(
          contacts,
          leads,
          applications,
          borrowers
        );
    }
  }, [contacts, leads, applications, borrowers, simpleContacts, filterType]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50
  });
  
  // Debug: Track pagination state changes
  useEffect(() => {
    logger.info('ContactsPipelineTable: Pagination state changed', pagination);
  }, [pagination]);
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false
    }
  ]);
  
  // Bulk assign dialog state
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [selectedContactsForAssign, setSelectedContactsForAssign] = useState<string[]>([]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // Add these to prevent pagination conflicts
    manualPagination: false,
    autoResetPageIndex: false,
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility
    }
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Search by name or email */}
          <div className="relative">
            <Input
              id={`${id}-input`}
              ref={inputRef}
              className={cn(
                "peer min-w-60 ps-9",
                Boolean(table.getColumn("name")?.getFilterValue()) && "pe-9"
              )}
              value={(table.getColumn("name")?.getFilterValue() ?? "") as string}
              onChange={(e) => table.getColumn("name")?.setFilterValue(e.target.value)}
              placeholder="Search contacts..."
              type="text"
              aria-label="Search contacts"
            />
            <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
              <ListFilter size={16} strokeWidth={2} aria-hidden="true" />
            </div>
            {Boolean(table.getColumn("name")?.getFilterValue()) && (
              <button
                className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Clear search"
                onClick={() => {
                  table.getColumn("name")?.setFilterValue("");
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
              >
                <CircleX size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <Select
            value={(table.getColumn("status")?.getFilterValue() as string) ?? "all"}
            onValueChange={(value) => {
              table.getColumn("status")?.setFilterValue(value === "all" ? undefined : value);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="New">New</SelectItem>
              <SelectItem value="Hot">Hot</SelectItem>
              <SelectItem value="Cool">Cool</SelectItem>
              <SelectItem value="Convert">Convert</SelectItem>
              <SelectItem value="Bad Information">Bad Information</SelectItem>
            </SelectContent>
          </Select>

          {/* Toggle columns visibility */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Columns3
                  className="-ms-1 me-2 opacity-60"
                  size={16}
                  strokeWidth={2}
                  aria-hidden="true"
                />
                View
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      onSelect={(event) => event.preventDefault()}
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-3">
          {/* Bulk Actions dropdown - positioned first but conditionally shown */}
          <div className="min-w-[140px]">
            {table.getSelectedRowModel().rows.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    Bulk Actions
                    <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                      {table.getSelectedRowModel().rows.length}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions for {table.getSelectedRowModel().rows.length} contacts</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      const selectedContacts = table.getSelectedRowModel().rows
                        .map(row => row.original.contactId || row.original.id)
                        .filter(id => id); // Filter out any undefined values
                      setSelectedContactsForAssign(selectedContacts);
                      setShowBulkAssignDialog(true);
                    }}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Transfer to Another Loan Officer
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="text-destructive focus:text-destructive"
                    onClick={async () => {
                      const selectedRows = table.getSelectedRowModel().rows;
                      const selectedContactIds = selectedRows
                        .map(row => row.original.contactId || row.original.id)
                        .filter(id => id); // Filter out any undefined values
                      const selectedNames = selectedRows.map(row => `${row.original.firstName} ${row.original.lastName}`).join(', ');
                      
                      if (onBulkDeleteContacts && selectedContactIds.length > 0 && confirmContactDeletion(`Are you sure you want to delete ${selectedContactIds.length} contact(s)?\n\n${selectedNames}`)) {
                        try {
                          await onBulkDeleteContacts(selectedContactIds);
                          table.resetRowSelection();
                        } catch (error) {
                          logger.error('Failed to delete contacts:', error);
                        }
                      }
                    }}
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    Delete Contacts
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="w-full"></div>
            )}
          </div>
          
          {/* Add Contact button - always in the same position */}
          {onAddContact && (
            <Button onClick={onAddContact}>
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border bg-background">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className="h-11"
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <div
                          className={cn(
                            header.column.getCanSort() &&
                              "flex h-full cursor-pointer select-none items-center justify-between gap-2"
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(e) => {
                            if (
                              header.column.getCanSort() &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              e.preventDefault();
                              header.column.getToggleSortingHandler()?.(e);
                            }
                          }}
                          tabIndex={header.column.getCanSort() ? 0 : undefined}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: (
                              <ChevronUp
                                className="shrink-0 opacity-60"
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            ),
                            desc: (
                              <ChevronDown
                                className="shrink-0 opacity-60"
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            )
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={(e) => {
                    // Don't trigger if clicking on checkbox, actions menu, or buttons
                    const target = e.target as HTMLElement;
                    const isInteractiveElement = target.closest('button, [role="checkbox"], [role="menuitem"], input, a');
                    if (!isInteractiveElement && onEditContact) {
                      onEditContact(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="last:py-0">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No contacts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-8">
        {/* Results per page */}
        <div className="flex items-center gap-3">
          <Label htmlFor={id} className="max-sm:sr-only">
            Rows per page
          </Label>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => {
              logger.info('ContactsPipelineTable: Page size change triggered', {
                oldPageSize: table.getState().pagination.pageSize,
                newValue: value,
                newPageSize: Number(value)
              });
              table.setPageSize(Number(value));
              logger.info('ContactsPipelineTable: Page size change completed', {
                currentPageSize: table.getState().pagination.pageSize
              });
            }}
          >
            <SelectTrigger id={id} className="w-fit whitespace-nowrap">
              <SelectValue placeholder="Select number of results" />
            </SelectTrigger>
            <SelectContent className="[&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
              {[10, 25, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={pageSize.toString()}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page number information */}
        <div className="flex grow justify-end whitespace-nowrap text-sm text-muted-foreground">
          <p className="whitespace-nowrap text-sm text-muted-foreground" aria-live="polite">
            <span className="text-foreground">
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
              {Math.min(
                Math.max(
                  table.getState().pagination.pageIndex * table.getState().pagination.pageSize +
                    table.getState().pagination.pageSize,
                  0
                ),
                table.getRowCount()
              )}
            </span>{" "}
            of <span className="text-foreground">{table.getRowCount().toString()}</span>
          </p>
        </div>

        {/* Pagination buttons */}
        <div>
          <Pagination>
            <PaginationContent>
              {/* First page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.firstPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="Go to first page"
                >
                  <ChevronFirst size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
              {/* Previous page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="Go to previous page"
                >
                  <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
              {/* Next page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label="Go to next page"
                >
                  <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
              {/* Last page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.lastPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label="Go to last page"
                >
                  <ChevronLast size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      {/* Bulk Assign Dialog */}
      <BulkAssignDialog
        open={showBulkAssignDialog}
        onOpenChange={setShowBulkAssignDialog}
        selectedContacts={selectedContactsForAssign}
        contactNames={table.getSelectedRowModel().rows.map(row => 
          `${row.original.firstName} ${row.original.lastName}`
        )}
        onAssign={async (officerId) => {
          if (onBulkAssignContacts) {
            try {
              await onBulkAssignContacts(selectedContactsForAssign, officerId);
              table.resetRowSelection();
              setSelectedContactsForAssign([]);
              setShowBulkAssignDialog(false);
            } catch (error) {
              logger.error('Failed to assign contacts:', error);
              throw error; // Re-throw to let BulkAssignDialog handle the error
            }
          }
        }}
      />
    </div>
  );
};
